import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PaymentMethodSelector = ({ selectedMethod, onMethodSelect, onPaymentSubmit, isProcessing }) => {
  const [activeTab, setActiveTab] = useState('cards');

  const paymentMethods = [
    {
      id: 'cards',
      name: 'Cards',
      icon: 'CreditCard',
      description: 'Credit & Debit Cards'
    },
    {
      id: 'upi',
      name: 'UPI',
      icon: 'Smartphone',
      description: 'Pay using UPI'
    },
    {
      id: 'netbanking',
      name: 'Net Banking',
      icon: 'Building2',
      description: 'Online Banking'
    },
    {
      id: 'wallets',
      name: 'Wallets',
      icon: 'Wallet',
      description: 'Digital Wallets'
    }
  ];

  const popularBanks = [
    { name: 'State Bank of India', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=60&fit=crop' },
    { name: 'HDFC Bank', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=60&fit=crop' },
    { name: 'ICICI Bank', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=60&fit=crop' },
    { name: 'Axis Bank', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=60&fit=crop' }
  ];

  const walletOptions = [
    { name: 'Paytm', logo: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=100&h=60&fit=crop' },
    { name: 'PhonePe', logo: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=100&h=60&fit=crop' },
    { name: 'Google Pay', logo: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=100&h=60&fit=crop' },
    { name: 'Amazon Pay', logo: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=100&h=60&fit=crop' }
  ];

  const handleMethodSelect = (methodId) => {
    setActiveTab(methodId);
    onMethodSelect(methodId);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-soft">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-semibold text-foreground">
          Payment Method
        </h2>
        <div className="flex items-center space-x-2">
          <Icon name="Shield" size={16} className="text-success" />
          <span className="text-sm font-medium text-success">256-bit SSL</span>
        </div>
      </div>

      {/* Payment Method Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-border">
        {paymentMethods.map((method) => (
          <button
            key={method.id}
            onClick={() => handleMethodSelect(method.id)}
            className={`flex items-center space-x-2 px-4 py-3 rounded-t-lg border-b-2 transition-micro ${
              activeTab === method.id
                ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <Icon name={method.icon} size={16} />
            <div className="text-left">
              <div className="font-medium text-sm">{method.name}</div>
              <div className="text-xs opacity-75">{method.description}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Payment Method Content */}
      <div className="space-y-6">
        {activeTab === 'cards' && (
          <div className="space-y-4">
            <div className="flex items-center space-x-4 mb-4">
              <div className="flex items-center space-x-2">
                <Icon name="CreditCard" size={20} className="text-primary" />
                <span className="font-medium text-foreground">Accepted Cards</span>
              </div>
              <div className="flex space-x-2">
                {['Visa', 'Mastercard', 'RuPay', 'American Express'].map((card) => (
                  <div key={card} className="w-8 h-5 bg-muted rounded border flex items-center justify-center">
                    <span className="text-xs font-bold text-muted-foreground">{card.slice(0, 2)}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Card Number
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="1234 5678 9012 3456"
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-micro"
                    maxLength="19"
                  />
                  <Icon name="CreditCard" size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Expiry Date
                  </label>
                  <input
                    type="text"
                    placeholder="MM/YY"
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-micro"
                    maxLength="5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    CVV
                  </label>
                  <input
                    type="password"
                    placeholder="123"
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-micro"
                    maxLength="4"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Cardholder Name
                </label>
                <input
                  type="text"
                  placeholder="Name as on card"
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-micro"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'upi' && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-48 h-48 mx-auto bg-muted rounded-lg flex items-center justify-center mb-4">
                <div className="text-center">
                  <Icon name="QrCode" size={64} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">QR Code will appear here</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Scan QR code with any UPI app
              </p>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-card text-muted-foreground">OR</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Enter UPI ID
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="yourname@upi"
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-micro"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2"
                >
                  Verify
                </Button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'netbanking' && (
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-foreground mb-3">Popular Banks</h4>
              <div className="grid grid-cols-2 gap-3">
                {popularBanks.map((bank, index) => (
                  <button
                    key={index}
                    className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-micro"
                  >
                    <div className="w-8 h-8 bg-muted rounded flex items-center justify-center">
                      <Icon name="Building2" size={16} className="text-muted-foreground" />
                    </div>
                    <span className="text-sm font-medium text-foreground">{bank.name}</span>
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Other Banks
              </label>
              <select className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-micro">
                <option value="">Select your bank</option>
                <option value="pnb">Punjab National Bank</option>
                <option value="bob">Bank of Baroda</option>
                <option value="canara">Canara Bank</option>
                <option value="union">Union Bank of India</option>
              </select>
            </div>
          </div>
        )}

        {activeTab === 'wallets' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {walletOptions.map((wallet, index) => (
                <button
                  key={index}
                  className="flex items-center space-x-3 p-4 border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-micro"
                >
                  <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                    <Icon name="Wallet" size={20} className="text-muted-foreground" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-foreground text-sm">{wallet.name}</div>
                    <div className="text-xs text-muted-foreground">Digital Wallet</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Security Features */}
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Shield" size={16} className="text-success" />
            <span className="font-medium text-foreground text-sm">Secure Payment</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="Check" size={12} className="text-success" />
              <span>256-bit SSL Encryption</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Check" size={12} className="text-success" />
              <span>PCI DSS Compliant</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Check" size={12} className="text-success" />
              <span>3D Secure Authentication</span>
            </div>
          </div>
        </div>

        {/* Payment Button */}
        <Button
          variant="default"
          size="lg"
          fullWidth
          loading={isProcessing}
          onClick={onPaymentSubmit}
          iconName="Lock"
          iconPosition="left"
        >
          {isProcessing ? 'Processing Payment...' : 'Pay Securely'}
        </Button>
      </div>
    </div>
  );
};

export default PaymentMethodSelector;